import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, TrendingUp, Brain } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';

interface QuizResult {
  quiz_id: string;
  quiz_title: string;
  correct_answers: number;
  total_questions: number;
  percentage: number;
  created_at: string;
}

const QuizResultsPage = () => {
  const { user } = useAuth();
  const [results, setResults] = useState<QuizResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [averageScore, setAverageScore] = useState<number>(0);

  useEffect(() => {
    const fetchResults = async () => {
      if (!user) return;

      try {
        // Fetch user responses joined with questions and quiz information
        const { data, error: fetchError } = await supabase
          .from('user_responses')
          .select(`
            created_at,
            is_correct,
            questions!inner (
              quiz_id,
              quiz!inner (
                title
              )
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;

        // Process and group the results by quiz
        const quizResults = new Map<string, {
          title: string;
          correct: number;
          total: number;
          latest_attempt: string;
        }>();

        data?.forEach(response => {
          const quizId = response.questions.quiz_id;
          const quizTitle = response.questions.quiz.title;
          const current = quizResults.get(quizId) || {
            title: quizTitle,
            correct: 0,
            total: 0,
            latest_attempt: response.created_at
          };

          current.correct += response.is_correct ? 1 : 0;
          current.total += 1;

          quizResults.set(quizId, current);
        });

        // Convert to array and calculate percentages
        const formattedResults: QuizResult[] = Array.from(quizResults.entries()).map(([quizId, result]) => ({
          quiz_id: quizId,
          quiz_title: result.title,
          correct_answers: result.correct,
          total_questions: result.total,
          percentage: (result.correct / result.total) * 100,
          created_at: result.latest_attempt
        }));

        // Calculate overall average
        const totalScore = formattedResults.reduce((acc, curr) => acc + curr.percentage, 0);
        const average = formattedResults.length > 0 ? totalScore / formattedResults.length : 0;

        setResults(formattedResults);
        setAverageScore(average);
      } catch (err) {
        console.error('Error fetching quiz results:', err);
        setError('Une erreur est survenue lors du chargement des résultats');
      } finally {
        setIsLoading(false);
      }
    };

    fetchResults();
  }, [user]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow sm:rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 bg-gray-50">
          <div className="flex items-center">
            <Trophy className="h-6 w-6 text-secondary-600 mr-2" />
            <h1 className="text-2xl font-bold text-gray-900">Mes résultats aux quiz</h1>
          </div>
        </div>

        <div className="px-4 py-5 sm:p-6">
          {error ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-6">
              <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Vous n'avez pas encore réalisé de quiz</p>
              <Link
                to="/mes-fiches"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary-600 hover:bg-secondary-700"
              >
                Découvrir les quiz disponibles
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="bg-gray-50 rounded-lg p-6 flex items-center justify-between">
                  <div className="flex items-center">
                    <TrendingUp className="h-8 w-8 text-secondary-600 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Score moyen global</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {averageScore.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Quiz
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                        Score
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                        Pourcentage
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {results.map((result) => (
                      <tr key={result.quiz_id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {result.quiz_title}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                          {result.correct_answers} / {result.total_questions}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                          {result.percentage.toFixed(1)}%
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                          <Link
                            to={`/quiz/${result.quiz_id}/solution`}
                            className="text-secondary-600 hover:text-secondary-900"
                          >
                            Voir les réponses
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizResultsPage;